<script setup lang="ts">
/**
 * 任務進度頁（`/jobs/:jobId`）——派工的「看得見」那一半。
 *
 * 三個要點：
 * 1. **2 秒增量 log**：每輪帶上一次的 `log_cursor` 當 `?since=`，只拉新行後 append；
 *    後端單批上限 2000 行（`JOB_LOG_PAGE`），拉滿就代表還有下一批。
 * 2. **終結態停止輪詢**——succeeded／failed／canceled／expired 之後 job 不會再變，
 *    繼續打 API 純浪費（唯一例外：那一輪 log 正好拉滿，還要再拉一輪把尾巴收完）。
 * 3. **階段條**吃 `lib/jobs.ts` 的序列（開站 12 步／升級 4 步）；沒有序列的 kind 退回
 *    「派出 → 執行中 → 完成」粗流程，不假造步驟。
 */
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import UiButton from '@/components/UiButton.vue'
import { ApiError } from '@/api/client'
import * as hosting from '@/api/hosting'
import type { HostJob, HostJobDetail, JobLogRow } from '@/api/hosting'
import {
  buildPhaseSteps,
  canRetryJob,
  diagnosticsPurgedAt,
  fmtDuration,
  isCancelableJob,
  isRetryableJobState,
  isUncancelableDecommission,
  NO_RETRY_JOB_KINDS,
  isTerminalJobState,
  jobAbnormalPhase,
  jobElapsed,
  jobKindLabel,
  jobPhaseLabel,
  jobStateClass,
  jobStateLabel,
  logLevelClass,
} from '@/lib/jobs'
import { usePolling } from '@/lib/poll'
import { fmtSeen } from '@/lib/staleness'
import { useHostingRoute } from '@/lib/hostingRoutes'
import { useAuthStore } from '@/stores/auth'
import { useDialogsStore } from '@/stores/dialogs'

/**
 * 代管路由名的視角分流（批99-B）：我方員工＝`host-detail`，代理商＝`reseller-host-detail`。
 * 本頁兩個視角共用同一支元件，所以每個連結都得問一次自己現在是誰。
 */
const hr = useHostingRoute()

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const dialogs = useDialogsStore()

const jobId = computed(() => String(route.params.jobId ?? ''))
const job = ref<HostJob | null>(null)
const logs = ref<JobLogRow[]>([])
const loading = ref(true)
const loadError = ref('')
const busy = ref(false)
const now = ref(Date.now())
/** 已停止自動更新（job 終結）——畫面要講清楚，不然看起來像卡住。 */
const stoppedPolling = ref(false)

let cursor = 0

function errText(e: unknown, fallback: string): string {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  return typeof d === 'string' ? d : JSON.stringify(d)
}

// --- log 面板：自動跟隨底部（往上捲即暫停，捲回底部即恢復）---
const logBox = ref<HTMLElement | null>(null)
const following = ref(true)

function onLogScroll() {
  const el = logBox.value
  if (!el) return
  following.value = el.scrollHeight - el.scrollTop - el.clientHeight < 24
}
function scrollToBottom() {
  const el = logBox.value
  if (el) el.scrollTop = el.scrollHeight
}
function toggleFollow() {
  following.value = !following.value
  if (following.value) void nextTick(scrollToBottom)
}

async function load() {
  if (!jobId.value) return
  try {
    const fresh: HostJobDetail = await hosting.getHostJob(jobId.value, cursor)
    const { logs: newLogs, log_cursor: nextCursor, ...rest } = fresh
    job.value = rest
    if (newLogs.length) {
      logs.value = logs.value.concat(newLogs)
      if (following.value) void nextTick(scrollToBottom)
    }
    cursor = nextCursor
    now.value = Date.now()
    loadError.value = ''
    // 終結態就收手；唯一例外＝這輪 log 拉滿（可能還有尾巴）→ 再多跑一輪。
    if (isTerminalJobState(rest.state) && newLogs.length < hosting.JOB_LOG_PAGE) {
      poll.stop()
      stoppedPolling.value = true
    }
  } catch (e) {
    loadError.value = errText(e, '讀取任務失敗')
  } finally {
    loading.value = false
  }
}

const poll = usePolling(load, 2_000)

watch(jobId, () => {
  job.value = null
  logs.value = []
  cursor = 0
  loading.value = true
  stoppedPolling.value = false
  following.value = true
  poll.start()
  void poll.refresh()
})

// --- 顯示衍生 ---
const steps = computed(() => (job.value ? buildPhaseSteps(job.value) : []))
const abnormal = computed(() => (job.value ? jobAbnormalPhase(job.value) : null))
const elapsed = computed(() => (job.value ? fmtDuration(jobElapsed(job.value, now.value)) : '—'))
const failed = computed(
  () => !!job.value && (job.value.state === 'failed' || job.value.state === 'expired'),
)
/**
 * 取消／重試（批99-B）：我方 admin 以上，**或代理商**（限自家主機的任務；
 * `isAdminOrAbove` 對代理商恆為 false，所以要顯式加上）。
 */
const canManage = computed(() => auth.isAdminOrAbove || auth.isReseller)
// 批150：已被 agent 領走的回收不給取消（後端 409；中途停下＝站台半毀、授權與 DNS 不收尾）
const canCancel = computed(() => !!job.value && canManage.value && isCancelableJob(job.value))
const decomRunning = computed(() => !!job.value && isUncancelableDecommission(job.value))
const canRetry = computed(() => !!job.value && canRetryJob(job.value, auth.roleRank))
/**
 * 批150：回收／停站／復站／改站台設定**不能直接重試**（後端 409）——重試不會重跑專用端點的
 * 前置檢查與確認。這幾種結束在可重試狀態時，改印一句指路，而不是讓人找不到重試鈕。
 */
const retryViaHost = computed(
  () => !!job.value && isRetryableJobState(job.value.state) && NO_RETRY_JOB_KINDS.has(job.value.kind),
)
/** 粗流程（沒有階段序列的 kind）：三格燈。 */
const coarse = computed(() => {
  const j = job.value
  if (!j || steps.value.length) return []
  const done = j.state === 'succeeded'
  const started = !!j.started_at
  const aborted = isTerminalJobState(j.state) && !done
  return [
    { label: '已派出', status: 'done' as const },
    {
      label: '執行中',
      status: done ? ('done' as const) : started ? ('current' as const) : ('pending' as const),
    },
    {
      label: '完成',
      status: done ? ('done' as const) : aborted ? ('skipped' as const) : ('pending' as const),
    },
  ]
})

function prettyJson(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2)
  } catch {
    return String(v)
  }
}

const paramsText = computed(() => (job.value?.params ? prettyJson(job.value.params) : ''))
const resultText = computed(() => (job.value?.result ? prettyJson(job.value.result) : ''))
/** 診斷內容已逾保存期清除（批150）：清除時刻；沒清過＝null。 */
const purgedAt = computed(() => (job.value ? diagnosticsPurgedAt(job.value) : null))

// --- 動作 ---
async function cancel() {
  const j = job.value
  if (!j) return
  const ok = await dialogs.confirm({
    title: '取消任務',
    message: '已經在跑的動作會在 agent 下次回報時中止，做到一半的步驟不會自動回復。確定取消？',
    danger: true,
    confirmText: '取消任務',
  })
  if (!ok) return
  busy.value = true
  try {
    await hosting.cancelHostJob(j.job_id)
    await poll.refresh()
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, '取消失敗') })
  } finally {
    busy.value = false
  }
}

async function retry() {
  const j = job.value
  if (!j) return
  busy.value = true
  try {
    const fresh = await hosting.retryHostJob(j.job_id)
    await router.push({ name: hr('job-detail'), params: { jobId: fresh.job_id } })
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, '重試失敗') })
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="page">
    <div class="crumb">
      <RouterLink :to="{ name: hr('jobs') }">← 任務</RouterLink>
    </div>

    <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>
    <div v-else-if="!job" class="card">
      <div class="empty">
        <div class="empty-title">找不到這筆任務</div>
        <div class="empty-sub">{{ loadError || '任務編號可能有誤。' }}</div>
      </div>
    </div>

    <template v-else>
      <p v-if="loadError" class="err">{{ loadError }}（顯示的是最後一次成功讀取的資料）</p>

      <!-- 頁首 -->
      <div class="card">
        <div class="head-top">
          <h1>{{ jobKindLabel(job.kind) }}</h1>
          <span class="status-pill" :class="jobStateClass(job.state)">{{ jobStateLabel(job.state) }}</span>
          <code class="mono">{{ job.job_id }}</code>
          <span v-if="job.signed" class="chip sig" title="帶 IPOJOB1 指令簽章：agent 離線驗章＋比對參數雜湊才執行">
            已簽章
          </span>
          <span class="spacer" />
          <UiButton v-if="canCancel" :loading="busy" @click="cancel">取消任務</UiButton>
          <UiButton v-if="canRetry" variant="primary" :loading="busy" @click="retry">重試</UiButton>
        </div>
        <div class="meta">
          <div class="mi">
            <span class="mk">主機</span>
            <span class="mv">
              <RouterLink :to="{ name: hr('host-detail'), params: { id: job.host_id } }">
                {{ job.host_name ?? job.host_id.slice(0, 8) }}
              </RouterLink>
            </span>
          </div>
          <div class="mi"><span class="mk">站台</span><span class="mv">{{ job.site_slug ?? '（主機層任務）' }}</span></div>
          <div class="mi"><span class="mk">發起人</span><span class="mv">{{ job.requested_by ?? '—' }}</span></div>
          <div class="mi"><span class="mk">建立</span><span class="mv">{{ fmtSeen(job.created_at) }}</span></div>
          <div class="mi"><span class="mk">開跑</span><span class="mv">{{ fmtSeen(job.started_at) }}</span></div>
          <div class="mi"><span class="mk">結束</span><span class="mv">{{ fmtSeen(job.finished_at) }}</span></div>
          <div class="mi"><span class="mk">耗時</span><span class="mv">{{ elapsed }}</span></div>
          <div class="mi">
            <span class="mk">嘗試次數</span>
            <span class="mv">{{ job.attempts }} / {{ job.max_attempts }}</span>
          </div>
          <div class="mi"><span class="mk">逾時時點</span><span class="mv">{{ fmtSeen(job.deadline_at) }}</span></div>
        </div>
        <p v-if="decomRunning" class="purged decom-running">
          回收已由 agent 執行中，不能取消（中途停下會留下半毀的站台，授權與 DNS 也不會收尾）；請等它結束——成功即已回收，失敗時站台會標成失敗、可再處理。
        </p>
        <p v-if="retryViaHost" class="purged retry-via-host">
          這類任務不能直接重試：請回<RouterLink :to="{ name: hr('host-detail'), params: { id: job.host_id } }">主機頁</RouterLink>重新執行「{{ jobKindLabel(job.kind) }}」（會重新做前置檢查與確認）。
        </p>
      </div>

      <!-- 階段條 -->
      <div class="card">
        <div class="card-head">
          <h3 class="section-title">流程</h3>
          <span class="spacer" />
          <span class="subtle">
            目前階段：{{ jobPhaseLabel(job.phase) }}
            <template v-if="job.progress">（{{ job.progress }}%）</template>
          </span>
        </div>

        <div v-if="abnormal" class="abn">⚠ {{ jobPhaseLabel(abnormal) }}：這不是正常流程，請看下方 log 與錯誤訊息。</div>

        <ol v-if="steps.length" class="steps">
          <li v-for="(s, i) in steps" :key="s.phase" class="step" :class="[s.status, { bad: failed && s.status === 'current' }]">
            <span class="dot">{{ i + 1 }}</span>
            <span class="slabel">{{ s.label }}</span>
          </li>
        </ol>
        <ol v-else class="steps">
          <li v-for="(s, i) in coarse" :key="s.label" class="step" :class="[s.status, { bad: failed && s.status === 'current' }]">
            <span class="dot">{{ i + 1 }}</span>
            <span class="slabel">{{ s.label }}</span>
          </li>
        </ol>
        <p v-if="!steps.length" class="subtle">
          這類任務的 agent 不回報細階段，只有粗流程與 log。
        </p>

        <div class="pbar big"><span class="pfill" :style="{ width: `${job.progress}%` }" /></div>
        <p v-if="job.detail" class="detail">{{ job.detail }}</p>
      </div>

      <!-- 失敗資訊 -->
      <div v-if="job.error || resultText" class="card">
        <div class="card-head">
          <h3 class="section-title">{{ job.error ? '錯誤' : '執行結果' }}</h3>
          <span class="spacer" />
          <UiButton v-if="canRetry" size="sm" variant="primary" :loading="busy" @click="retry">重試</UiButton>
        </div>
        <p v-if="job.error" class="errbox">{{ job.error }}</p>
        <p v-if="purgedAt" class="purged">
          診斷內容（站台日誌尾段等）已逾保存期，於 {{ fmtSeen(purgedAt) }} 清除；任務紀錄與其餘欄位保留。
          需要最新狀況請重新收集診斷。
        </p>
        <pre v-if="resultText" class="json">{{ resultText }}</pre>
      </div>

      <!-- log -->
      <div class="card">
        <div class="card-head">
          <h3 class="section-title">執行紀錄</h3>
          <span class="count">{{ logs.length }}</span>
          <span class="spacer" />
          <span v-if="stoppedPolling" class="subtle">任務已結束，停止自動更新</span>
          <span v-else class="subtle">每 2 秒拉新行</span>
          <UiButton size="sm" @click="toggleFollow">{{ following ? '暫停跟隨' : '跟隨最新' }}</UiButton>
        </div>
        <div v-if="!logs.length" class="empty">
          <div class="empty-sub">
            {{ job.state === 'queued' ? 'agent 還沒領走這筆任務（long-poll 最長 60 秒）。' : '尚無執行紀錄。' }}
          </div>
        </div>
        <div v-else ref="logBox" class="logbox" @scroll="onLogScroll">
          <div v-for="l in logs" :key="l.seq" class="lrow" :class="logLevelClass(l.level)">
            <span class="lseq">{{ l.seq }}</span>
            <span class="lat">{{ fmtSeen(l.at, { short: true }) }}</span>
            <span class="ltext">{{ l.text }}</span>
          </div>
        </div>
      </div>

      <!-- 參數（不含機密：授權 blob 走 agent 專用端點另取）-->
      <div v-if="paramsText" class="card">
        <div class="card-head"><h3 class="section-title">參數</h3></div>
        <pre class="json">{{ paramsText }}</pre>
        <p class="subtle">參數內不含機密——授權 blob 由 agent 另呼 `/api/agent/v1/sites/{id}/license` 取得。</p>
      </div>
    </template>
  </div>
</template>

<style scoped>
.crumb {
  margin-bottom: var(--sp-3);
  font-size: var(--fs-12);
}
.crumb a {
  color: var(--fg-3);
  text-decoration: none;
}
.crumb a:hover {
  color: var(--fg);
}
.err {
  margin: 0 0 var(--sp-3);
  color: var(--danger);
  font-size: var(--fs-12);
}
.head-top {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-wrap: wrap;
}
.head-top h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.spacer {
  flex: 1;
}
.chip.sig {
  background: var(--st-transferred-bg);
  border-color: transparent;
  color: var(--st-transferred-fg);
}
.meta {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: var(--sp-2) var(--sp-4);
  margin-top: var(--sp-4);
}
.mi {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.mk {
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.mv {
  font-size: var(--fs-13);
  color: var(--fg);
  overflow-wrap: anywhere;
}
.mv a {
  color: var(--brand-700);
  text-decoration: none;
}
[data-theme='dark'] .mv a {
  color: var(--brand-400);
}
.mv a:hover {
  text-decoration: underline;
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}

/* 階段條：**換行不橫捲**——開站有 12 步，橫捲會把後半段藏起來（看不到才是最糟的）。 */
.steps {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: var(--sp-2);
  list-style: none;
  margin: 0 0 var(--sp-3);
  padding: 0;
}
.step {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: none;
  padding-right: var(--sp-3);
  position: relative;
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.step + .step {
  padding-left: var(--sp-3);
}
/* 步與步之間的連接線 */
.step + .step::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: var(--sp-3);
  height: 1px;
  background: var(--border-strong);
}
.dot {
  display: inline-grid;
  place-items: center;
  width: 20px;
  height: 20px;
  flex: none;
  border-radius: 999px;
  border: 1px solid var(--border-strong);
  background: var(--bg);
  font-size: var(--fs-11);
  font-weight: 700;
  color: var(--fg-4);
}
.step.done .dot {
  background: var(--st-active-bg);
  border-color: transparent;
  color: var(--st-active-fg);
}
.step.done .slabel {
  color: var(--fg-3);
}
.step.current .dot {
  background: var(--brand-500);
  border-color: var(--brand-500);
  color: var(--fg-inv);
}
.step.current .slabel {
  color: var(--fg);
  font-weight: 700;
}
.step.current.bad .dot {
  background: var(--danger);
  border-color: var(--danger);
  color: #fff;
}
.step.current.bad .slabel {
  color: var(--danger);
}
.step.skipped {
  opacity: 0.45;
}
.abn {
  margin: 0 0 var(--sp-3);
  padding: 8px 12px;
  border-radius: var(--r-sm);
  background: rgba(209, 67, 67, 0.12);
  color: var(--danger);
  font-size: var(--fs-12);
  font-weight: 600;
}
.pbar {
  height: 5px;
  border-radius: 999px;
  background: var(--bg-sunken);
  overflow: hidden;
}
.pbar.big {
  height: 8px;
}
.pfill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: var(--brand-500);
  transition: width var(--dur-base) var(--ease);
}
.detail {
  margin: var(--sp-2) 0 0;
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.errbox {
  margin: 0;
  padding: 10px 12px;
  border-radius: var(--r-sm);
  background: rgba(209, 67, 67, 0.1);
  color: var(--danger);
  font-size: var(--fs-12);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
/* 診斷內容已逾保存期清除（批150）：中性提示，不是錯誤。 */
.purged {
  margin: var(--sp-2) 0 0;
  padding: 8px 12px;
  border-radius: var(--r-sm);
  background: var(--bg);
  border: 1px solid var(--border-2);
  color: var(--fg-2);
  font-size: var(--fs-12);
}
/* 批150：回收／停站／復站／改設定不能直接重試——指路回主機頁（同一個中性提示框）。 */
.retry-via-host,
.decom-running {
  margin-top: var(--sp-3);
}
.purged a {
  color: var(--brand-700);
}
[data-theme='dark'] .purged a {
  color: var(--brand-400);
}
.json {
  margin: var(--sp-3) 0 0;
  padding: 10px 12px;
  max-height: 260px;
  overflow: auto;
  border-radius: var(--r-sm);
  background: var(--bg);
  border: 1px solid var(--border-2);
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: var(--fs-11);
  color: var(--fg-2);
}
.logbox {
  max-height: 420px;
  overflow: auto;
  border-radius: var(--r-sm);
  background: var(--bg);
  border: 1px solid var(--border-2);
  padding: 6px 0;
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: var(--fs-11);
  line-height: 1.55;
}
.lrow {
  display: grid;
  grid-template-columns: 48px 84px 1fr;
  gap: var(--sp-2);
  padding: 1px 12px;
  color: var(--fg-2);
}
.lrow:hover {
  background: var(--bg-hover);
}
.lseq,
.lat {
  color: var(--fg-4);
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.ltext {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.lrow.lg-warn .ltext {
  color: var(--warning);
}
.lrow.lg-error .ltext {
  color: var(--danger);
  font-weight: 600;
}
.lrow.lg-dim {
  opacity: 0.7;
}
</style>
